import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import { DynamicMediaItem } from '@uncinc/react-drupal-core';

import { Container, Icon, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import {
  ActionButton,
  ButtonWrapper,
  ParagraphsRenderer,
  SearchThemes,
} from '../../../../../components';
import useLayoutVariables from '../../../../../hooks/useLayoutVariables';
import type { ThemeProps } from '..';

import './style.scss';

const TaxonomyThemePageParent: React.FC<ThemeProps> = ({ entity }) => {
  const { t } = useTranslation();

  const {
    heroImageAspectRatio,
    heroTextColClasses,
    heroMediaColClasses,
    textColClasses,
    searchThemesColClasses,
  } = useLayoutVariables('themeParent');

  const theme = entity.toJS();
  const tid = theme.tid[0].value;
  const name = theme.name[0].value;
  const description = theme.description[0].value;
  const link = theme.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const media = theme.field_media_item[0];
  const labels = theme.field_labels;
  const paragraphs = theme.field_paragraphs;
  const colorClass = theme.field_labels[0]?.entity.field_icon[0]?.classes ?? 'u-background--secondary--400';
  const iconName = theme.field_labels[0]?.entity.field_icon[0]?.icon;

  const classes = c('Taxonomy', 'TaxonomyTheme', 'TaxonomyTheme--Parent', labels.map(
    (label) => `TaxonomyTheme--label--${label.entity.name[0].value}`,
  ));

  const heroClasses = c('TaxonomyTheme__hero', colorClass);

  return (
    <div className={classes}>
      <div className={heroClasses}>
        <Container>
          <div className="row">
            <div className={heroTextColClasses}>
              <div className="TaxonomyTheme__hero__text-wrapper">
                <div className="TaxonomyTheme__hero__text">
                  {iconName && (
                    <div className="TaxonomyTheme__icon">
                      <Icon name={iconName} />
                    </div>
                  )}

                  <h1 className="TaxonomyTheme__title" itemProp="title">
                    {name}
                  </h1>
                </div>
              </div>
            </div>

            {media && (
              <div className={heroMediaColClasses}>
                <div className="TaxonomyTheme__hero__media-wrapper">
                  <div className="TaxonomyTheme__hero__media">
                    <DynamicMediaItem
                      media={media}
                      imageAspectRatio={heroImageAspectRatio}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="TaxonomyTheme__hero__action-buttons-wrapper">
              <div className="TaxonomyTheme__hero__action-buttons">
                <ActionButton
                  className="TaxonomyTheme__hero__skip-to-content-button"
                  href="#main"
                  iconName="arrow_downward"
                  aria-label={t('content_types.homepage.skip_to_content')}
                />
              </div>
            </div>
          </div>
        </Container>
      </div>

      {(description || linkUrl) && (
        <Container>
          <div id="main" className="row">
            <div className={textColClasses}>
              <div className="TaxonomyTheme__text-wrapper">
                <div className="TaxonomyTheme__text">
                  {description && (
                    <WysiwygField
                      className="TaxonomyTheme__description body--large"
                      property="schema:text"
                      html={description}
                    />
                  )}

                  {linkUrl && (
                    <div className="TaxonomyTheme__link">
                      <ButtonWrapper href={linkUrl}>
                        {linkTitle}
                      </ButtonWrapper>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Container>
      )}

      {/* View of child taxonomy term teasers */}
      <SearchThemes parentId={tid}>
        <Container>
          <div className="row">
            <div className={searchThemesColClasses}>
              <SearchThemes.Results
                className="TaxonomyTheme__search-themes"
                title={t('taxonomies.theme.search_themes_title', { name })}
              />
            </div>
          </div>
        </Container>
      </SearchThemes>

      <ParagraphsRenderer className="TaxonomyTheme__paragraphs" paragraphs={paragraphs} />
    </div>
  );
};

export default TaxonomyThemePageParent;
